import type { PropertyValuePayload } from './property-value.js';
import { isPropertyValuePayload } from './property-value.js';

export const DATE_FORMATS = ['full', 'short', 'ymd'] as const;

export type DateFormat = (typeof DATE_FORMATS)[number];

export const DATE_FORMAT_LABELS: Record<DateFormat, string> = {
  full: 'Full date',
  short: 'Short date',
  ymd: 'Year-Month-Day',
};

/** Maps legacy stored values (iso, long, us, eu) to current formats. */
const LEGACY_DATE_FORMAT: Record<string, DateFormat> = {
  iso: 'ymd',
  long: 'full',
  us: 'full',
  eu: 'full',
};

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

export type ParsedDateValue = {
  start: string | null;
  end: string | null;
};

export function isDateFormat(value: unknown): value is DateFormat {
  return typeof value === 'string' && (DATE_FORMATS as readonly string[]).includes(value);
}

export function coerceDateFormat(value: unknown): DateFormat {
  if (typeof value === 'string' && value in LEGACY_DATE_FORMAT) {
    return LEGACY_DATE_FORMAT[value]!;
  }
  if (isDateFormat(value)) {
    return value;
  }
  return 'full';
}

export function dateFormatFromConfig(config: Record<string, unknown> | undefined): DateFormat {
  return coerceDateFormat(config?.dateFormat);
}

/** Extract start/end ISO dates from a date cell payload. */
export function parseDatePayload(
  payload: PropertyValuePayload | Record<string, unknown> | null | undefined,
): ParsedDateValue {
  if (!payload || !isPropertyValuePayload(payload) || payload.t !== 'date') {
    return { start: null, end: null };
  }
  const start = isoDateOnly(payload.v);
  const end =
    typeof payload.end === 'string' ? isoDateOnly(payload.end) : null;
  return { start, end };
}

export function isoDateOnly(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }
  const match = value.trim().match(ISO_DATE);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : null;
}

function formatOneIso(iso: string, format: DateFormat): string {
  const parts = iso.split('-').map(Number);
  const y = parts[0] ?? 0;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  switch (format) {
    case 'full':
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    case 'short':
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    case 'ymd':
    default:
      return iso;
  }
}

export function formatDateValue(
  payload: PropertyValuePayload | Record<string, unknown> | null | undefined,
  format: DateFormat = 'full',
): string {
  const { start, end } = parseDatePayload(payload);
  if (!start) {
    return '';
  }
  const a = formatOneIso(start, format);
  if (!end || end === start) {
    return a;
  }
  const b = formatOneIso(end, format);
  return `${a} – ${b}`;
}

/** Parse user-typed date text into ISO `YYYY-MM-DD` (date-only). */
export function parseDateInput(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) {
    return null;
  }
  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }
  const slashUs = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashUs) {
    const month = slashUs[1]!.padStart(2, '0');
    const day = slashUs[2]!.padStart(2, '0');
    return `${slashUs[3]}-${month}-${day}`;
  }
  const parsed = Date.parse(trimmed);
  if (!Number.isNaN(parsed)) {
    const d = new Date(parsed);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
  return null;
}

export function encodeDateCellValue(
  start: string | null,
  end?: string | null,
): PropertyValuePayload | null {
  const startIso = start ? isoDateOnly(start) : null;
  if (!startIso) {
    return null;
  }
  const endIso = end ? isoDateOnly(end) : null;
  if (endIso && endIso !== startIso) {
    const [a, b] = endIso < startIso ? [endIso, startIso] : [startIso, endIso];
    return { t: 'date', v: a, end: b };
  }
  return { t: 'date', v: startIso };
}

export function normalizeDateFormatForSchema(
  config: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!config) {
    return {};
  }
  const raw = config.dateFormat;
  if (raw === undefined) {
    return config;
  }
  return { ...config, dateFormat: coerceDateFormat(raw) };
}
